import { useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/stores/auth";
import { initials } from "@/lib/utils";

/** Map pathname to page title */
function pageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    "/dashboard": "Dashboard",
    "/conversations": "Conversations",
    "/customers": "Customers",
    "/payments": "Payments",
    "/knowledge-base": "Knowledge Base",
    "/channels": "Channels",
    "/analytics": "Analytics",
    "/billing": "Billing",
    "/settings": "Settings",
    "/onboarding": "Setup",
  };
  for (const [prefix, title] of Object.entries(titles)) {
    if (pathname.startsWith(prefix)) return title;
  }
  return "Concierge AI";
}

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const logout = useAuthStore((s) => s.logout);
  const title = pageTitle(location.pathname);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="flex items-center justify-between h-16 px-6 border-b border-border bg-background">
      {/* Left — Title + tenant */}
      <div>
        <h1 className="text-lg font-semibold">{title}</h1>
        {tenant && <p className="text-xs text-muted-foreground -mt-0.5">{tenant.name}</p>}
      </div>

      {/* Right — Profile */}
      <div className="flex items-center gap-3">
        {/* Profile Dropdown */}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-9 w-9 rounded-full"
                aria-label="User menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs bg-primary/20 text-primary">
                    {initials(user.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user.name}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
